<script setup lang="ts">
import { computed, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import NavList, { type NavGroup } from '@/components/NavList.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import BlockUserForm from '@/components/sharingSettings/BlockUserForm.vue'
import CountList from '@/components/sharingSettings/CountList.vue'
import EntryList from '@/components/sharingSettings/EntryList.vue'
import { useSharingEditor } from '@/composables/useSharingEditor'
import { groupByCalendar, groupByGrantee } from '@/settings/grantGroups'
import { grantLabel } from '@/settings/grantLabel'
import { parseSharingPath, sharingPath } from '@/settings/sharingNav'

// The Sharing & blocked users category of the Settings (`/settings/blocked[/granted[/calendars[/<id>]|users[/<key>]]|/users[/add]]`):
// picks the page from the route and prints that page's own description above it. The data is read when the category opens.
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const editor = useSharingEditor()
const view = computed(() => parseSharingPath(route.params.rest))

const calendarGroups = computed(() => groupByCalendar(editor.grants.value))
const granteeGroups = computed(() => groupByGrantee(editor.grants.value, (g) => grantLabel(g, t)))
const calendarGroup = computed(() => {
  const v = view.value
  return v.view === 'calendar' ? calendarGroups.value.find((g) => g.calendarId === v.calendarId) : undefined
})
const granteeGroup = computed(() => {
  const v = view.value
  return v.view === 'user' ? granteeGroups.value.find((g) => g.key === v.key) : undefined
})

// (a stale link, or the last entry of a group removed, goes back to the list of groups)
watchEffect(() => {
  const v = view.value
  if (v.view === 'unknown') void router.replace(sharingPath())
  else if (editor.loaded.value && v.view === 'calendar' && !calendarGroup.value) void router.replace(sharingPath('granted', 'calendars'))
  else if (editor.loaded.value && v.view === 'user' && !granteeGroup.value) void router.replace(sharingPath('granted', 'users'))
})

const count = (n: number) => t('settings.blocked.itemsCount', { n }, n)
const indexGroups = computed<NavGroup[]>(() => [
  {
    key: 'sharing',
    items: [
      { to: sharingPath('granted'), icon: '🔓', title: t('settings.blocked.grantedTitle'), value: count(editor.grantCount.value) },
      { to: sharingPath('users'), icon: '🚫', title: t('settings.blocked.blockedTitle'), value: count(editor.blockedCount.value) },
    ],
  },
])
const grantedGroups = computed<NavGroup[]>(() => [
  {
    key: 'granted',
    items: [
      { to: sharingPath('granted', 'calendars'), icon: '📅', title: t('settings.blocked.byCalendar'), hint: t('settings.blocked.byCalendarHint'), value: count(calendarGroups.value.length) },
      { to: sharingPath('granted', 'users'), icon: '👤', title: t('settings.blocked.byUser'), hint: t('settings.blocked.byUserHint'), value: count(granteeGroups.value.length) },
    ],
  },
])

onMounted(() => editor.load())
</script>

<template>
  <p v-if="view.view !== 'unknown'" class="ui-hint page-hint">{{ t(`settings.blocked.pageHint.${view.view}`) }}</p>
  <p v-if="editor.loadError.value" class="error">{{ editor.loadError.value }}</p>
  <p v-else-if="!editor.loaded.value">{{ t('common.loading') }}</p>
  <template v-else>
    <NavList v-if="view.view === 'index'" :groups="indexGroups" />
    <NavList v-else-if="view.view === 'granted'" :groups="grantedGroups" />
    <CountList
      v-else-if="view.view === 'calendars'"
      :items="calendarGroups.map((g) => ({ key: String(g.calendarId), to: sharingPath('granted', 'calendars', g.calendarId), title: g.name, count: g.grants.length }))"
      :empty-text="t('settings.blocked.grantedNone')"
    />
    <CountList
      v-else-if="view.view === 'users'"
      :items="granteeGroups.map((g) => ({ key: g.key, to: sharingPath('granted', 'users', g.key), title: grantLabel(g.sample, t), count: g.grants.length }))"
      :empty-text="t('settings.blocked.grantedNone')"
    />
    <EntryList
      v-else-if="view.view === 'calendar' && calendarGroup"
      :items="calendarGroup.grants.map((g) => ({ id: g.id, title: grantLabel(g, t) }))"
      :action-label="t('common.remove')"
      :empty-text="t('settings.blocked.grantedNone')"
      @action="(id) => editor.revokeGrant(calendarGroup!.grants.find((g) => g.id === id)!)"
    />
    <EntryList
      v-else-if="view.view === 'user' && granteeGroup"
      :items="granteeGroup.grants.map((g) => ({ id: g.id, title: g.calendar_name }))"
      :action-label="t('common.remove')"
      :empty-text="t('settings.blocked.grantedNone')"
      @action="(id) => editor.revokeGrant(granteeGroup!.grants.find((g) => g.id === id)!)"
    />
    <EntryList
      v-else-if="view.view === 'blocked'"
      :items="editor.blocked.value.map((b) => ({ id: b.id, title: `@${b.blocked_username ?? b.blocked_telegram_user_id}` }))"
      :action-label="t('settings.blocked.unblock')"
      :empty-text="t('settings.blocked.none')"
      @action="(id) => editor.unblock(editor.blocked.value.find((b) => b.id === id)!)"
    >
      <template #above>
        <UiGroup>
          <UiList>
            <UiRow :to="sharingPath('users', 'add')" class="add" data-testid="add-blocked">＋ {{ t('settings.blocked.addUser') }}</UiRow>
          </UiList>
        </UiGroup>
      </template>
    </EntryList>
    <BlockUserForm v-else-if="view.view === 'blockedAdd'" />
  </template>
</template>

<style scoped>
.page-hint {
  margin: 0 0.25rem 0.75rem;
}

.add {
  color: var(--tg-link-color);
  text-decoration: none;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
